import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, CreditCard, HelpCircle, LogOut, Wrench, DollarSign, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
export function ProfileDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    signOut
  } = useAuth();
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  const handleLogout = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  // Shared styles for menu items
  const menuItemBase = `
    flex items-center gap-3 px-4 py-2.5 text-sm font-bold uppercase tracking-wider
    transition-all duration-200 ease-out
    focus:outline-none focus-visible:bg-gray-900 focus-visible:text-white
  `;
  const menuItemInteractive = `
    ${menuItemBase}
    text-gray-400 
    hover:bg-gray-900 hover:text-white
    active:bg-gray-800
  `;
  return <div className="relative" ref={dropdownRef} data-id="element-1014" data-mp="2ky587szvqkgn">
      {/* Trigger Button */}
      <button onClick={() => setIsOpen(!isOpen)} className={`
          relative flex items-center justify-center
          w-10 h-10 border
          transition-all duration-200 ease-out
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]
          ${isOpen ? 'bg-[#FF0000]/10 border-[#FF0000]/50 text-[#FF0000]' : 'bg-[#0a0a0a] border-gray-700 text-gray-400 hover:text-white hover:border-[#FF0000]/50'}
        `} aria-label="User menu" aria-expanded={isOpen} aria-haspopup="true" data-id="element-1015" data-mp="2kw282g50jay7">
        <User size={18} strokeWidth={2} data-id="element-1016" data-mp="3kq0qyek9t1zp" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && <div className="
            absolute right-0 mt-2 w-64
            bg-[#0a0a0a] 
            shadow-2xl shadow-black/50
            border border-gray-800
            py-1.5 z-50
            origin-top-right
            animate-in fade-in-0 zoom-in-95 slide-in-from-top-2
            duration-200 ease-out
          " role="menu" aria-orientation="vertical" data-id="element-1017" data-mp="3pov9dfebsoja">
          {/* Section Header */}
          <div className="px-4 py-2.5 bg-[#050505] border-b border-gray-800" data-id="element-1018" data-mp="1j034gpfa4oez">
            <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-1019" data-mp="1e0yq7kdi00hs">
              Account
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1" data-id="element-1020" data-mp="26q5rgr4c746q">
            <Link to="/settings" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-1021" data-mp="26pe6sbca2qv8">
              <Settings size={16} strokeWidth={2} className="text-gray-600" data-id="element-1022" data-mp="1wrgkhtdzb459" />
              <span data-id="element-1023" data-mp="1rtf0954tgys7">Settings</span>
            </Link>

            {/* Affiliate Program - Disabled */}
            <div className={`
                ${menuItemBase}
                justify-between
                text-gray-600 cursor-not-allowed
              `} role="menuitem" aria-disabled="true" data-id="element-1024" data-mp="lcuo18u4y5ju">
              <div className="flex items-center gap-3" data-id="element-1025" data-mp="256w1l2bjxs7y">
                <DollarSign size={16} strokeWidth={2} className="text-gray-600" data-id="element-1026" data-mp="29zfbnpb16580" />
                <span data-id="element-1027" data-mp="1v7wrdfb51oiu">Affiliate Program</span>
              </div>
              <span className="text-[10px] font-bold bg-gray-900 text-gray-600 px-2 py-0.5 border border-gray-800" data-id="element-1028" data-mp="20a5jyak3mokf">
                SOON
              </span>
            </div>

            <Link to="/premium-tools" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-1029" data-mp="2syb6n2upqnmw">
              <Wrench size={16} strokeWidth={2} className="text-gray-600" data-id="element-1030" data-mp="2izbxzk9y6rzw" />
              <span data-id="element-1031" data-mp="2j1ewytt2vvgr">Tools</span>
            </Link>
          </div>

          {/* Divider */}
          <div className="my-1.5 mx-3 border-t border-gray-800" data-id="element-1032" data-mp="12ngetru3nvuc" />

          {/* Billing & Support Section */}
          <div className="py-1" data-id="element-1033" data-mp="17i2i17q12i3c">
            <Link to="/settings?tab=billing" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-1034" data-mp="3lreryfua6w4t">
              <CreditCard size={16} strokeWidth={2} className="text-gray-600" data-id="element-1035" data-mp="3ut5vpf59gsw1" />
              <span data-id="element-1036" data-mp="3qrkbf8qedcbt">Billing</span>
            </Link>

            <a href="mailto:support@toxicdetector.com" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-1037" data-mp="3qrsniyep4dly">
              <HelpCircle size={16} strokeWidth={2} className="text-gray-600" data-id="element-1038" data-mp="2y4gbvja2pq7f" />
              <span data-id="element-1039" data-mp="3butjtlmyt4wr">Support</span>
            </a>
          </div>

          {/* Divider */}
          <div className="my-1.5 mx-3 border-t border-gray-800" data-id="element-1040" data-mp="3728rypnmd1vv" />

          {/* Logout */}
          <div className="py-1" data-id="element-1041" data-mp="2jcnxb9i3sg6r">
            <button onClick={handleLogout} className={`
                ${menuItemBase}
                w-full text-left
                text-[#FF0000]
                hover:bg-[#FF0000]/10 hover:text-[#FF0000]
                active:bg-[#FF0000]/20
              `} role="menuitem" data-id="element-1042" data-mp="2jbwez0dioeyf">
              <LogOut size={16} strokeWidth={2} data-id="element-1043" data-mp="s1ktud1ixf2l" />
              <span data-id="element-1044" data-mp="s1t4rze36ab5">Logout</span>
            </button>
          </div>
        </div>}
    </div>;
}